import { useEffect, useMemo, useState } from "react";
import { Space } from "~/lib/types";
import {
  Box,
  Breadcrumbs,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ComplianceTable } from "~/components/Completion/ComplianceTable";
import { FormatDate, FormatRelativeDate } from "~/lib/date";
import { useReports } from "~/pages/reports/hooks/useReports";
import {
  DEFAULT_PAGE_SIZE,
  INITIAL_PAGE_RANGE,
  PAGE_SIZE_PARAM,
} from "~/components/pagination";
import { ReportsEmptyState } from "~/pages/reports/components/ReportsEmptyState";
import {
  Delete as DeleteIcon,
  Home as HomeIcon,
  SaveAlt as SaveAltIcon,
} from "@mui/icons-material";
import { ContentFlag } from "~/components/ui-library";
import { DocumentStatus, IntegrationMessage } from "~/operations";
import { LoadingFailed, LoadingPage } from "~/components/loading";
import { AddButton } from "~/components/add-button";
import { AnimatePresence } from "motion/react";
import { getDocumentTypeText } from "~/lib/generateReportUtils";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { ConfirmationDialog } from "~/components/ConfirmationDialog/ConfirmationDialog";
import { pluralize } from "~/lib/pluralize";
import { setDocumentTitle } from "~/lib/commonUtils";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import ReportTypesPicker from "~/pages/reports/components/ReportTypePicker/ReportTypesPicker";
import { Header } from "~/types/table";

type Props = {
  space: Space;
};

export function Reports({ space }: Props) {
  const [pageItems, setPageItems] = useState(INITIAL_PAGE_RANGE);
  const [openGenerateDialog, setOpenGenerateDialog] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const pageSizeParam = Number(searchParams.get(PAGE_SIZE_PARAM));
  const triageToolbar = useTriageToolbar();

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?spaceId=${space.id}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Typography key={1}>Reporting</Typography>,
  ];

  const {
    selectedReports,
    handle,
    documentList: { loading, error, reports = [], data },
    deleteDialog,
  } = useReports({
    space,
    onDeleteSucceed: () => {
      // We need this for delete bulk reports to reload the table
      setPageItems({ from: 0, to: pageSizeParam || DEFAULT_PAGE_SIZE });
    },
  });

  useEffect(() => {
    if (selectedReports.length > 0) {
      triageToolbar.show({
        buttons: [
          {
            type: TriageToolbarButtonType.Delete,
            onClick: handle.delete,
          },
        ],
        payload: {
          count: selectedReports.length,
          totalCount: data?.totalCount,
          onClose: handle.cancel,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selectedReports]);

  const paginatedReports = reports.slice(pageItems.from, pageItems.to);

  // Get only completed and failed reports for the current page
  const finishedPaginatedReports = useMemo(
    () =>
      paginatedReports.filter(
        (report) =>
          report.status === DocumentStatus.Completed ||
          report.status === DocumentStatus.Failed,
      ),
    [paginatedReports],
  );

  // All completed and failed reports on current page are selected
  const allFinishedSelectedReports = useMemo(
    () =>
      finishedPaginatedReports.length > 0 &&
      finishedPaginatedReports.every((report) =>
        selectedReports.includes(report.mrn),
      ),
    [finishedPaginatedReports, selectedReports],
  );

  // Some but not all completed and failed reports on current page are selected
  const someFinishedSelectedReports = useMemo(
    () =>
      !allFinishedSelectedReports &&
      finishedPaginatedReports.some((report) =>
        selectedReports.includes(report.mrn),
      ),
    [allFinishedSelectedReports, finishedPaginatedReports, selectedReports],
  );

  const tableHeaders: Header[] = [
    {
      id: "SELECT",
      label: (
        <Checkbox
          onChange={(_, checked) =>
            handle.selectAll(checked, pageItems.from, pageItems.to)
          }
          checked={allFinishedSelectedReports}
          indeterminate={someFinishedSelectedReports}
        />
      ),
      sortable: false,
    },
    { id: "NAME", label: "Document name" },
    { id: "TYPE", label: "Report type" },
    { id: "CREATED_BY", label: "Created by" },
    { id: "CREATED_AT", label: "Date created" },
    { id: "ACTIONS", label: "", sortable: false },
  ];

  const isSingleDelete = useMemo(
    () => selectedReports.length === 1,
    [selectedReports.length],
  );

  const getReportName = (mrn: string) => {
    const report = reports.find((r) => r.mrn === mrn);
    return report?.name || mrn;
  };

  const renderDeleteDialogContent = () => {
    if (isSingleDelete) {
      const reportName = reports.find(
        (report) => report.mrn === selectedReports?.[0],
      )?.name;
      return (
        <Typography variant="body1">
          Are you sure you want to delete the report "{reportName}"? This action
          cannot be undone.
        </Typography>
      );
    }

    // Bulk report deletion
    return (
      <Box>
        <Typography variant="body1" mb={2}>
          Are you sure you want to delete {selectedReports.length}{" "}
          {pluralize("report", selectedReports.length)}? This action cannot be
          undone.
        </Typography>
        {selectedReports.length <= 5 && (
          <Box component="ul" ml={2}>
            {selectedReports.map((mrn) => (
              <Typography component="li" key={mrn} variant="body2">
                {getReportName(mrn)}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  setDocumentTitle(["Reporting", space.name]);

  if (loading && !data) {
    return <LoadingPage what="reports" />;
  }

  if (error) {
    return <LoadingFailed what="reports" />;
  }

  return (
    <Grid container>
      <Grid size={12}>
        <Breadcrumbs sx={{ mb: 3 }} separator="›">
          {breadcrumbs}
        </Breadcrumbs>
      </Grid>
      <Grid
        mb={openGenerateDialog ? 0 : 4}
        style={{ display: "flex", justifyContent: "space-between" }}
        size={12}
      >
        <Typography
          variant="h4"
          component="h1"
          fontWeight={700}
          sx={{ textTransform: "uppercase", fontSize: { xs: 22, sm: 32 } }}
        >
          {openGenerateDialog ? "Generate Reports" : "Reporting"}
        </Typography>

        <AddButton
          id="generate-report"
          aria-label="Generate Reports"
          close={openGenerateDialog}
          onClick={() => setOpenGenerateDialog(!openGenerateDialog)}
        />
      </Grid>
      <AnimatePresence mode="wait">
        {openGenerateDialog && (
          <ReportTypesPicker
            space={space}
            onSuccess={() => setOpenGenerateDialog(false)}
          />
        )}

        {!openGenerateDialog && (
          <Grid size={12}>
            {data && !loading && !reports.length ? (
              <ReportsEmptyState />
            ) : (
              <>
                <ComplianceTable
                  tableHeaders={tableHeaders}
                  selectable={true}
                  selection={selectedReports}
                  paginationProps={{
                    fetchMore: handle.fetchMore,
                    pageInfo: data?.pageInfo,
                    setPageItems,
                    totalCount: data?.totalCount || 0,
                    goToFirstPage: pageItems.from === 0,
                  }}
                >
                  {paginatedReports.map((report) => {
                    //Determine if the report is Pending, Queued, or Running
                    const jobInProcess = [
                      DocumentStatus.Pending,
                      DocumentStatus.Queued,
                      DocumentStatus.Running,
                    ].includes(report.status);

                    const jobPending = report.status === DocumentStatus.Pending;
                    const jobFailure = report.status === DocumentStatus.Failed;
                    const requestedAt = `${FormatRelativeDate(
                      report.requestedAt,
                    )} - ${FormatDate(report.requestedAt)}`;

                    return (
                      <TableRow
                        key={report.mrn}
                        sx={{
                          height: "70px",
                          // only display the icon buttons on hover
                          "& .action-button": {
                            opacity: 0,
                          },
                          "&:hover": {
                            "& .action-button": {
                              opacity: 1,
                            },
                          },
                          ...(jobInProcess && {
                            // if the job is in process, make the row grey and the text lighter
                            "& .MuiTableCell-root": {
                              color: "text.secondary",
                              opacity: 0.5,
                              cursor: "default",
                            },
                            // hide the checkbox when the job is in process
                            "& .select-checkbox": {
                              display: "none",
                            },
                            // if the job is in process, hide the delete and download buttons
                            "& .action-button": {
                              display: "none",
                            },
                          }),
                        }}
                      >
                        <TableCell>
                          {!jobInProcess && (
                            <Checkbox
                              className="select-checkbox"
                              onChange={(_e, checked) =>
                                handle.selectionChange(checked, report.mrn)
                              }
                              checked={selectedReports.includes(report.mrn)}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {report.name}
                            {report.status === DocumentStatus.Queued && (
                              <ContentFlag
                                flag="queued"
                                color="info"
                                layout={{ ml: 1 }}
                              />
                            )}
                            {report.status === DocumentStatus.Running && (
                              <>
                                <CircularProgress
                                  size={16}
                                  sx={{ ml: 1, color: "text.primary" }}
                                />
                                <ContentFlag
                                  flag="running"
                                  color="info"
                                  layout={{ ml: 1 }}
                                />
                              </>
                            )}
                            {jobPending && (
                              <ContentFlag
                                flag="pending"
                                color="warning"
                                layout={{ ml: 1 }}
                              />
                            )}
                            {jobFailure && (
                              <JobFailureFlag messages={report.messages} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {getDocumentTypeText(report.type)}
                        </TableCell>
                        <TableCell>{report.createdBy?.name}</TableCell>
                        <TableCell
                          sx={{ fontSize: (theme) => theme.spacing(1.25) }}
                        >
                          {requestedAt}
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: (theme) => theme.spacing(1),
                            }}
                          >
                            {/* If the download fails, we'll show a retry icon in place of the download icon */}
                            <Tooltip
                              title={jobFailure ? "Retry" : "Download"}
                              placement="top"
                              arrow
                            >
                              <span>
                                {!jobFailure && (
                                  <IconButton
                                    className="action-button"
                                    onClick={() =>
                                      handle.downloadSingle(report)
                                    }
                                  >
                                    <SaveAltIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </span>
                            </Tooltip>

                            <Tooltip title="Delete" placement="top" arrow>
                              <span>
                                <IconButton
                                  className="action-button"
                                  onClick={() =>
                                    handle.deleteSingle(report.mrn)
                                  }
                                >
                                  <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </ComplianceTable>
              </>
            )}
          </Grid>
        )}
      </AnimatePresence>
      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onConfirm={deleteDialog.confirmDelete}
        onClose={deleteDialog.close}
        loading={deleteDialog.isDeleting}
        confirmButtonText="Delete"
        title={isSingleDelete ? "Delete Report" : "Delete Reports"}
        content={<>{renderDeleteDialogContent()}</>}
      />
    </Grid>
  );
}

type JobFailureFlagProps = {
  messages?: IntegrationMessage[] | null;
};

const JobFailureFlag = ({ messages }: JobFailureFlagProps) => {
  // Get the most recent message (last in the array)
  const latestMessage = messages?.at(-1)?.message;
  const tooltipMessage =
    latestMessage || "Mondoo couldn't generate this report. Please try again.";

  return (
    <Tooltip
      title={tooltipMessage}
      placement="top"
      arrow
      PopperProps={{
        sx: {
          ".MuiTooltip-tooltip": {
            maxWidth: 220,
            textAlign: "center",
          },
        },
      }}
    >
      <span>
        <ContentFlag flag="error" color="error" layout={{ ml: 1 }} />
      </span>
    </Tooltip>
  );
};
